export class ParseContext {
	input = ''
	offset = 0
	data = {} // always persists, even if rollback

	constructor(input, offset = 0, data = null) {
		this.input = input;
		this.offset = offset;
		this.data = data;
	}

	copy() { return new ParseContext(this.input, this.offset, this.data); }
	shift(n) { return this.offset += n; }
}

export class Matcher {
	match(ctx) { return MatchResult.fail(); }

	parse(input, data = null) {
		let ctx = new ParseContext(input, 0, data);
		let result = this.match(ctx);
		if (result.isOk()) {
			return result.value;
		} else {
			throw result.value;
		}
	}

	map(mapper) {
		return simpleMatcher(ctx => {
			let initialOffset = ctx.offset;
			let result = this.match(ctx);
			if (result.isOk()) {
				result.value = mapper(result.value);
				return result;
			}

			ctx.offset = initialOffset;
			return genericFail;
		});
	}
}

export class MatchResult {
	type = 0 // 0 - ok, 1 - fail
	value = null

	constructor(type, value) {
		this.type = type;
		this.value = value;
	}

	static ok(value) { return new MatchResult(0, value); }
	static fail(error) { return new MatchResult(1, error); }

	isOk() { return this.type === 0; }
	isFail() { return this.type === 1; }
}

const genericFail = MatchResult.fail();

export function simpleMatcher(f) {
	let matcher = new Matcher();
	matcher.match = f;
	return matcher;
}

// will rollback offset on fail
export function rollbackableMatcher(f) {
	return simpleMatcher(ctx => {
		let startOffset = ctx.offset;
		let result = f(ctx);
		if (result.isFail()) {
			ctx.offset = startOffset;
		}
		return result;
	});
}

export const lazy = f => {
	let matcher = simpleMatcher(ctx => {
		let realMatcher = f(ctx);
		matcher.match = ctx2 => realMatcher.match(ctx2);
		return realMatcher.match(ctx);
	});

	return matcher;
};

export const str = value => simpleMatcher(ctx => {
	if (ctx.input.substr(ctx.offset, value.length) === value) {
		ctx.shift(value.length);
		return MatchResult.ok(value);
	}

	return genericFail;
});

export const lookupableRegex = rx => {
	let rxglobal = new RegExp(rx, rx.global ? rx.flags : rx.flags + 'g');

	return simpleMatcher(ctx => {
		rxglobal.lastIndex = ctx.offset;
		let result = rxglobal.exec(ctx.input);

		if (result === null || result.index !== ctx.offset) {
			return genericFail;
		}

		ctx.shift(result[0].length);
		return MatchResult.ok(result);
	});
};

export const regex = rx => {
	let rxglobal = new RegExp(rx);

	return simpleMatcher(ctx => {
		rxglobal.lastIndex = 0;
		let result = rxglobal.exec(ctx.input.slice(ctx.offset));

		if (result === null || result.index !== 0) {
			return genericFail;
		}

		ctx.shift(result[0].length);
		return MatchResult.ok(result);
	});
};

export const first = matchers => simpleMatcher(ctx => {
	let initialOffset = ctx.offset;

	for (let i = 0; i < matchers.length; ++i) {
		let result = matchers[i].match(ctx);
		if (result.isOk()) {
			return result;
		}
		ctx.offset = initialOffset;
	}

	return genericFail;
});

export const all = matchers => simpleMatcher(ctx => {
	let initialOffset = ctx.offset;
	let results = [];

	for (let i = 0; i < matchers.length; ++i) {
		let result = matchers[i].match(ctx);
		if (result.isFail()) {
			ctx.offset = initialOffset;
			return genericFail;
		}
		results.push(result.value);
	}

	return MatchResult.ok(results);
});

export const many = matcher => simpleMatcher(ctx => {
	let results = [];

	while (true) {
		let initialOffset = ctx.offset;
		let result = matcher.match(ctx);
		if (result.isOk()) {
			results.push(result.value);
		} else {
			ctx.offset = initialOffset;
			break;
		}
	}

	return MatchResult.ok(results);
});

export const many1 = matcher => simpleMatcher(ctx => {
	let results = [];

	while (true) {
		let initialOffset = ctx.offset;
		let result = matcher.match(ctx);
		if (result.isOk()) {
			results.push(result.value);
		} else {
			ctx.offset = initialOffset;
			break;
		}
	}

	return results.length > 0 ? MatchResult.ok(results) : genericFail;
});